/** Settings → General (Figma "Settings · polished"): business, account, notifications, team, plan. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Building,
  ChartColumn,
  ChevronDown,
  CircleCheck,
  Clock,
  Crown,
  LockKeyhole,
  LogOut,
  Mail,
  Monitor,
  TriangleAlert,
  Users,
} from "lucide-react";
import { useId, useMemo, useState, type SelectHTMLAttributes } from "react";
import { Link, useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { useFormDraft } from "@/lib/form-drafts";
import {
  Alert,
  Badge,
  Button,
  ErrorAlert,
  Field,
  Input,
  Modal,
  PasswordInput,
  PasswordRequirements,
  Spinner,
  cx,
  Form,
  Section,
  Toggle,
  buttonClass,
  useToast,
} from "@/components/ui";
import { identity, portal, session } from "@/lib/api";
import { formatDateTime, timeAgo } from "@/lib/format";
import { ApiError } from "@/lib/http";
import { MAX_PASSWORD, meetsPasswordPolicy } from "@/lib/password-policy";
import { useOverview } from "@/lib/queries";
import { browserTimeZone } from "@/lib/time-zone";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { useTimeZoneChoice } from "@/lib/use-time-zone";
import type { Account, BusinessProfile, EmailPreferences, EmailUpdate } from "@/lib/types";

import { CloseAccountCard, DownloadDataRow } from "./CloseAccount";
import { MfaModal } from "./MfaModal";
import { MfaRecoveryNotice } from "./MfaRecoveryNotice";
import { CURRENCIES, INDUSTRIES, TIME_ZONES, zoneLabel } from "./options";
import { PanelHead, ReadOnlyNote, SettingRow, WeavexMark } from "./parts";

export function GeneralTab({ account }: { account: Account }) {
  return (
    <div className="flex flex-col items-start gap-4 lg:flex-row">
      <div className="flex w-full min-w-0 flex-1 flex-col gap-6">
        <BusinessCard />
        <AccountCard account={account} />
        <NotificationsCard />
      </div>
      <div className="flex w-full shrink-0 flex-col gap-5 lg:w-[344px]">
        <TeamCard />
        <PlanCard />
        <CloseBusinessCard />
        <CloseAccountCard account={account} />
      </div>
    </div>
  );
}

// ── business ────────────────────────────────────────────────────────────────
const fieldControl =
  "block w-full rounded-[10px] border border-[#d4cac4] bg-surface py-[11px] pr-3 pl-3.5 text-sm leading-5 font-medium text-fg " +
  "focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none disabled:opacity-60";

function SelectField({
  label,
  name,
  required,
  children,
  ...rest
}: { label: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <Field label={label} name={name} required={required}>
      {(p) => (
        <span className="relative block">
          <select {...p} {...rest} className={cx(fieldControl, "appearance-none pr-9")}>
            {children}
          </select>
          <ChevronDown
            aria-hidden
            className="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-[#5c5551]"
          />
        </span>
      )}
    </Field>
  );
}

export function BusinessCard() {
  const { orgId, current, can } = useOrganization();
  const profile = useQuery({ queryKey: ["business-profile", orgId], queryFn: () => portal.profile(orgId) });
  if (profile.isPending)
    return (
      <Section>
        <PanelHead icon={Building} title="Business" />
        <Spinner />
      </Section>
    );
  if (profile.isError)
    return (
      <Section>
        <PanelHead icon={Building} title="Business" />
        <ErrorAlert error={profile.error} />
      </Section>
    );
  // Remount when the organization changes so the form starts from its values.
  return (
    <BusinessForm
      key={orgId + (profile.data.updated_at ?? "")}
      profile={profile.data}
      name={current.organization.name}
      editable={can("organization:update")}
    />
  );
}

function BusinessForm({
  profile,
  name: savedName,
  editable,
}: {
  profile: BusinessProfile;
  name: string;
  editable: boolean;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const [start] = useState({
    name: savedName,
    zone: profile.time_zone ?? browserTimeZone(),
    industry: profile.industry ?? "",
    currency: profile.currency ?? "GBP",
  });
  const [name, setName] = useState(start.name);
  const [zone, setZone] = useState(start.zone);
  const [industry, setIndustry] = useState(start.industry);
  const [currency, setCurrency] = useState(start.currency);
  const draftValues = useMemo(() => ({ name, zone, industry, currency }), [name, zone, industry, currency]);
  const draft = useFormDraft("settings.business", draftValues, { ready: editable });
  const save = useMutation({
    mutationFn: async () => {
      if (name.trim() !== savedName) await identity.updateOrganization(orgId, { name: name.trim() });
      return portal.saveProfile(orgId, { time_zone: zone, industry: industry || null, currency });
    },
    onSuccess: (saved) => {
      draft.clear();
      qc.setQueryData(["business-profile", orgId], saved);
      void qc.invalidateQueries({ queryKey: ["organizations"] });
      toast.show({ tone: "success", title: "Business details saved" });
    },
  });
  const complete = Boolean(savedName && profile.time_zone && profile.industry && profile.currency);
  const dirty =
    name.trim() !== savedName ||
    zone !== profile.time_zone ||
    (industry || null) !== profile.industry ||
    currency !== profile.currency;
  const zones = TIME_ZONES.some(([z]) => z === zone) ? TIME_ZONES : [[zone, zoneLabel(zone)], ...TIME_ZONES];
  // Edited means different from what the form opened with and from what is saved: a business with no profile yet
  // opens on defaults, and the organisation's name can reach this form a moment after its own save.
  const edited = (value: string, opened: string, saved: string | null) => value !== opened && value !== saved;
  const unsaved = useUnsavedChanges(
    editable &&
      !save.isPending &&
      (edited(name.trim(), start.name, savedName) ||
        edited(zone, start.zone, profile.time_zone) ||
        edited(industry, start.industry, profile.industry ?? "") ||
        edited(currency, start.currency, profile.currency)),
  );

  return (
    <Section>
      {unsaved.modal}
      <PanelHead icon={Building} title="Business">
        <Badge size="md">{complete ? "Setup complete" : "Add details"}</Badge>
      </PanelHead>
      <Form
        className="flex flex-col gap-4"
        error={save.error}
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ErrorAlert error={save.error} />
        <DraftOffer
          draft={draft}
          onContinue={(d) => {
            setName(d.name);
            setZone(d.zone);
            setIndustry(d.industry);
            setCurrency(d.currency);
          }}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Business name" name="name" required={editable}>
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                disabled={!editable}
                className={fieldControl}
              />
            )}
          </Field>
          <SelectField
            label="Time zone"
            name="time_zone"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            disabled={!editable}
          >
            {zones.map(([z, label]) => (
              <option key={z} value={z}>
                {label}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Industry"
            name="industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            disabled={!editable}
          >
            <option value="">Choose an industry</option>
            {INDUSTRIES.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Currency"
            name="currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={!editable}
          >
            {CURRENCIES.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </SelectField>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            variant="brand"
            size="lg"
            requires="organization:update"
            loading={save.isPending}
            disabledReason={!dirty && profile.updated_at !== null ? "Change something to save." : undefined}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
          {editable && dirty && <DraftSavedNote savedAt={draft.savedAt} />}
          <ReadOnlyNote permission="organization:update" />
        </div>
      </Form>
    </Section>
  );
}

// ── account ─────────────────────────────────────────────────────────────────
function AccountCard({ account }: { account: Account }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<"profile" | "password" | "mfa" | "sessions" | null>(null);
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: identity.sessions });
  const signOut = useMutation({
    mutationFn: () => session.signOut(),
    onSettled: () => {
      qc.clear();
      navigate("/login", { replace: true });
    },
  });
  const others = (sessions.data ?? []).filter((s) => !s.current).length;
  return (
    <Section>
      <PanelHead icon={LockKeyhole} title="Account" />
      <MfaRecoveryNotice />
      <div className="flex flex-col gap-3.5">
        <SettingRow
          icon={<Mail className="size-[15px]" strokeWidth={1.75} />}
          title="Email"
          detail={
            <>
              {account.email}
              {!account.email_verified && " · not verified yet"}
            </>
          }
        >
          <Button
            unstyled
            type="button"
            className={buttonClass("secondary", "lg")}
            onClick={() => setOpen("profile")}
          >
            Change
          </Button>
        </SettingRow>
        <SettingRow
          icon={<LockKeyhole className="size-[15px]" strokeWidth={1.75} />}
          title="Password"
          detail={
            account.has_password
              ? "Changing it signs out your other devices"
              : "You sign in with Google or GitHub"
          }
        >
          <Button
            unstyled
            type="button"
            className={buttonClass("secondary", "lg")}
            onClick={() => setOpen("password")}
          >
            {account.has_password ? "Change" : "Set up"}
          </Button>
        </SettingRow>
        <SettingRow
          icon={<CircleCheck className="size-[15px]" strokeWidth={1.75} />}
          title="Two-factor authentication"
          detail={
            account.mfa_enabled ? "Required for security actions" : "Not set up — recommended for owners"
          }
        >
          <>
            <Badge size="md">{account.mfa_enabled ? "On" : "Off"}</Badge>
            <Button
              unstyled
              type="button"
              className={buttonClass("secondary", "lg")}
              onClick={() => setOpen("mfa")}
            >
              Manage
            </Button>
          </>
        </SettingRow>
        <TimeZoneRow />
        <SettingRow
          icon={<Monitor className="size-[15px]" strokeWidth={1.75} />}
          title="Signed-in devices"
          detail={
            sessions.isPending
              ? "Checking…"
              : others === 0
                ? "Only this device"
                : `This device and ${others} other${others === 1 ? "" : "s"}`
          }
        >
          <Button
            unstyled
            type="button"
            className={buttonClass("secondary", "lg")}
            onClick={() => setOpen("sessions")}
          >
            Manage
          </Button>
        </SettingRow>
        <DownloadDataRow />
        <SettingRow
          icon={<LogOut className="size-[15px]" strokeWidth={1.75} />}
          title="Log out"
          detail="Signs you out of this device only"
          danger
        >
          <Button
            unstyled
            type="button"
            className={buttonClass("dangerOutline", "lg")}
            disabled={signOut.isPending}
            aria-busy={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            Log out
          </Button>
        </SettingRow>
      </div>
      {open === "profile" && <ProfileModal account={account} onClose={() => setOpen(null)} />}
      {open === "password" && <PasswordModal account={account} onClose={() => setOpen(null)} />}
      {open === "mfa" && <MfaModal account={account} onClose={() => setOpen(null)} />}
      {open === "sessions" && <SessionsModal onClose={() => setOpen(null)} />}
    </Section>
  );
}

function ProfileModal({ account, onClose }: { account: Account; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(account.display_name);
  const save = useMutation({
    mutationFn: () => identity.updateMe({ display_name: name.trim() }),
    onSuccess: (updated) => {
      qc.setQueryData(["me"], updated);
      session.updateUser(updated);
      toast.show({ tone: "success", title: "Profile saved" });
      onClose();
    },
  });
  const resend = useMutation({ mutationFn: identity.resendVerification });
  const unsaved = useUnsavedChanges(name.trim() !== account.display_name && !save.isPending);
  const close = () => unsaved.guard(onClose);
  return (
    <>
      <Modal
        open
        onClose={close}
        title="Your profile"
        footer={
          <>
            <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="profile-form"
              variant="brand"
              size="lg"
              loading={save.isPending}
              disabledReason={name.trim() === account.display_name ? "Change your name to save." : undefined}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <Form
          id="profile-form"
          className="space-y-4"
          error={save.error}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <ErrorAlert error={save.error ?? resend.error} />
          <Field label="Name" name="display_name" required hint="Shown to your teammates.">
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />}
          </Field>
          <Field
            label="Email"
            hint={
              account.email_verified
                ? "Verified. To use a different address, contact support."
                : "Not verified yet."
            }
          >
            {(p) => <Input {...p} value={account.email} disabled />}
          </Field>
          {!account.email_verified &&
            (resend.isSuccess ? (
              <Alert tone="success">Verification email sent.</Alert>
            ) : (
              <Button
                unstyled
                type="button"
                className={buttonClass("secondary", "lg")}
                disabled={resend.isPending}
                aria-busy={resend.isPending}
                onClick={() => resend.mutate()}
              >
                Resend verification email
              </Button>
            ))}
        </Form>
      </Modal>
      {unsaved.modal}
    </>
  );
}

function PasswordModal({ account, onClose }: { account: Account; onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const requirementsId = useId();
  const change = useMutation({ mutationFn: () => identity.changePassword(current, next) });
  const err = change.error instanceof ApiError ? change.error : null;
  const nextOk = meetsPasswordPolicy(next, account.email);
  if (!account.has_password) {
    return (
      <Modal open onClose={onClose} title="Password">
        <p className="text-sm text-fg-muted">
          You sign in with Google or GitHub. To add a password, sign out and use “Forgot password” on the
          sign-in page.
        </p>
      </Modal>
    );
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Change password"
      footer={
        change.isSuccess ? (
          <Button unstyled type="button" className={buttonClass("brand", "lg")} onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="password-form"
              variant="brand"
              size="lg"
              loading={change.isPending}
              disabledReason={nextOk ? undefined : "Choose a new password that meets every requirement."}
            >
              {change.isPending ? "Changing…" : "Change password"}
            </Button>
          </>
        )
      }
    >
      {change.isSuccess ? (
        <Alert tone="success">Password changed. Your other devices were signed out.</Alert>
      ) : (
        <Form
          id="password-form"
          className="space-y-4"
          error={change.error}
          onSubmit={(e) => {
            e.preventDefault();
            if (nextOk) change.mutate();
          }}
        >
          {/* field errors go on their fields and in the summary, so the ErrorAlert stays quiet for them */}
          {err && err.reason !== "PASSWORD_BREACHED" && <ErrorAlert error={err} />}
          <Field
            label="Current password"
            name="current_password"
            required
            error={err?.reason === "INVALID_CREDENTIALS" ? "Incorrect password" : undefined}
          >
            {(p) => (
              <PasswordInput
                {...p}
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="New password"
            name="new_password"
            required
            error={
              err?.reason === "PASSWORD_BREACHED"
                ? "This password appeared in a data breach. Choose another."
                : undefined
            }
          >
            {(p) => (
              <>
                <PasswordInput
                  {...p}
                  aria-describedby={[p["aria-describedby"], requirementsId].filter(Boolean).join(" ")}
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  maxLength={MAX_PASSWORD}
                />
                <PasswordRequirements id={requirementsId} password={next} email={account.email} />
              </>
            )}
          </Field>
        </Form>
      )}
    </Modal>
  );
}

function SessionsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: identity.sessions });
  const toast = useToast();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["sessions"] });
  const revoke = useMutation({
    mutationFn: identity.revokeSession,
    onSuccess: () => {
      refresh();
      toast.show({ tone: "success", title: "Device signed out" });
    },
  });
  const revokeOthers = useMutation({
    mutationFn: identity.revokeOtherSessions,
    onSuccess: () => {
      refresh();
      toast.show({ tone: "success", title: "Other devices signed out" });
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Signed-in devices"
      footer={
        (sessions.data?.length ?? 0) > 1 && (
          <Button
            unstyled
            type="button"
            className={buttonClass("secondary", "lg")}
            disabled={revokeOthers.isPending}
            aria-busy={revokeOthers.isPending}
            onClick={() => revokeOthers.mutate()}
          >
            Sign out other devices
          </Button>
        )
      }
    >
      <ErrorAlert error={sessions.error ?? revoke.error ?? revokeOthers.error} />
      {sessions.isPending ? (
        <Spinner />
      ) : (
        <ul className="-my-2 divide-y divide-border">
          {(sessions.data ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-3 py-3">
              <Monitor className="size-4 shrink-0 text-fg-subtle" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-fg" title={s.user_agent ?? undefined}>
                  {describeAgent(s.user_agent)} {s.current && <Badge tone="success">This device</Badge>}
                </p>
                <p className="text-xs text-fg-subtle">
                  {s.ip_address ?? "Unknown IP"} · active {timeAgo(s.last_seen_at)} · signed in{" "}
                  {formatDateTime(s.created_at)}
                </p>
              </div>
              {!s.current && (
                <Button
                  unstyled
                  type="button"
                  className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-fg-muted hover:bg-muted hover:text-fg"
                  disabled={revoke.isPending && revoke.variables === s.id}
                  aria-busy={revoke.isPending && revoke.variables === s.id}
                  onClick={() => revoke.mutate(s.id)}
                >
                  Sign out
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

export function describeAgent(ua: string | null): string {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /iPhone|iPad/.test(ua)
      ? "iOS"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Android/.test(ua)
          ? "Android"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return os ? `${browser} on ${os}` : browser;
}

// ── notifications (summary of Settings → Notifications) ─────────────────────
export function useEmailPreferences() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: ["email-preferences", orgId], queryFn: () => portal.emailPreferences(orgId) });
}

function NotificationsCard() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const prefs = useEmailPreferences();
  const toast = useToast();
  const key = ["email-preferences", orgId];
  const save = useMutation({
    mutationFn: (next: EmailPreferences) => portal.saveEmailPreferences(orgId, next),
    onMutate: (next) => qc.setQueryData(key, next),
    onSuccess: (saved) => {
      qc.setQueryData(key, saved);
      toast.show({ id: "email-preferences-saved", tone: "success", title: "Notification settings saved" });
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const set = (k: EmailUpdate, on: boolean) =>
    prefs.data && save.mutate({ ...prefs.data, updates: { ...prefs.data.updates, [k]: on } });
  const frequency = { daily: "every morning", weekly: "every Monday", monthly: "on the 1st of each month" };

  return (
    <Section>
      <PanelHead icon={Bell} title="Notifications">
        <Link
          to="/settings?tab=notifications"
          className="text-[13px] font-semibold text-brand-600 hover:underline"
        >
          All alerts
        </Link>
      </PanelHead>
      <ErrorAlert error={prefs.error ?? save.error} />
      {prefs.data ? (
        <div className="flex flex-col gap-3.5">
          <SettingRow
            icon={<ChartColumn className="size-[15px]" strokeWidth={1.75} />}
            title={`${prefs.data.digest_frequency === "weekly" ? "Weekly" : prefs.data.digest_frequency === "daily" ? "Daily" : "Monthly"} performance digest`}
            detail={`Email summary ${frequency[prefs.data.digest_frequency]}`}
          >
            <Toggle
              size="lg"
              tone="dark"
              label="Performance digest"
              checked={prefs.data.updates.digest}
              onChange={(on) => set("digest", on)}
            />
          </SettingRow>
          <SettingRow
            icon={<TriangleAlert className="size-[15px]" strokeWidth={1.75} />}
            title="Needs-attention alerts"
            detail="Immediate email for payment or domain issues · always on"
          >
            <Toggle
              size="lg"
              tone="dark"
              label="Needs-attention alerts (always on)"
              checked
              disabledReason="These alerts are always on, so you never miss a payment or domain problem."
              onChange={() => {}}
            />
          </SettingRow>
          <SettingRow
            icon={<WeavexMark className="size-[15px]" />}
            title="Weavex suggestions"
            detail="Email when Weavex has a new recommendation"
          >
            <Toggle
              size="lg"
              tone="dark"
              label="Weavex suggestions"
              checked={prefs.data.updates.weavex_suggestions}
              onChange={(on) => set("weavex_suggestions", on)}
            />
          </SettingRow>
        </div>
      ) : (
        !prefs.isError && <Spinner />
      )}
    </Section>
  );
}

// ── right column ────────────────────────────────────────────────────────────
function TeamCard() {
  const { orgId } = useOrganization();
  const members = useQuery({ queryKey: ["members", orgId], queryFn: () => identity.members(orgId) });
  const shown = members.data?.items.slice(0, 4) ?? [];
  const more = (members.data?.items.length ?? 0) - shown.length;
  return (
    <Section>
      <PanelHead icon={Users} title="Team & roles" size="md">
        <Link to="/team" className={buttonClass("secondary", "lg")}>
          Manage
        </Link>
      </PanelHead>
      {members.isPending ? (
        <Spinner />
      ) : members.isError ? (
        <p className="text-[13px] text-fg-subtle">See who has access in Team &amp; roles.</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {shown.map((m) => (
            <li key={m.user.id} className="flex items-center gap-3">
              <MemberAvatar name={m.user.display_name || m.user.email} url={m.user.avatar_url} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="truncate text-sm font-semibold text-fg">
                  {m.user.display_name || m.user.email}
                </p>
                <p className="truncate text-xs text-fg-subtle">
                  {m.roles.map((r) => r.name).join(", ") || "Member"} · {m.user.email}
                </p>
              </div>
            </li>
          ))}
          {more > 0 && (
            <li>
              <Link to="/team" className="text-[13px] font-semibold text-fg-muted hover:text-fg">
                +{more} more
              </Link>
            </li>
          )}
        </ul>
      )}
    </Section>
  );
}

function MemberAvatar({ name, url }: { name: string; url: string | null }) {
  if (url) return <img src={url} alt="" className="size-9 shrink-0 rounded-full object-cover" />;
  const letters = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-[#5c5551]">
      {letters}
    </span>
  );
}

function PlanCard() {
  const overview = useOverview();
  const plan = overview.data?.account.plan;
  return (
    <section className="flex flex-col gap-2.5 rounded-[14px] bg-brand-50 p-4">
      <div className="flex items-center gap-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[#fef4e5] text-[#995c05]">
          <Crown className="size-4" strokeWidth={1.75} />
        </span>
        <Link to="/billing" className="text-[15px] font-semibold text-fg hover:underline">
          {plan ? `${plan.name} plan` : "Your plan"}
        </Link>
      </div>
      <p className="text-[13px] text-[#766e6a]">
        Seat limits come from your plan. Roles and access are managed in{" "}
        <Link to="/team" className="underline hover:text-fg">
          Team &amp; roles
        </Link>
        .
      </p>
    </section>
  );
}

function CloseBusinessCard() {
  const [open, setOpen] = useState(false);
  return (
    <section className="flex flex-col gap-3 rounded-[14px] border border-[#ffc4bb] bg-[#fff1ee] p-4">
      <div className="flex items-center gap-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[#fbd6c9] text-[#d6342f]">
          <TriangleAlert className="size-4" strokeWidth={1.75} />
        </span>
        <h2 className="text-base font-semibold text-fg">Close this business</h2>
      </div>
      <p className="text-sm text-[#766e6a]">
        Permanently removes your website, domains and mailboxes. This can’t be undone.
      </p>
      <div>
        <Button variant="danger" size="lg" requires="organization:delete" onClick={() => setOpen(true)}>
          Close business
        </Button>
      </div>
      {open && <CloseBusinessModal onClose={() => setOpen(false)} />}
    </section>
  );
}

function CloseBusinessModal({ onClose }: { onClose: () => void }) {
  const { orgId, current } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const name = current.organization.name;
  const close = useMutation({
    mutationFn: () => identity.deleteOrganization(orgId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["organizations"] });
      navigate("/", { replace: true });
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Close this business?"
      footer={
        <>
          <Button unstyled type="button" className={buttonClass("secondary", "lg")} onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="danger"
            size="lg"
            loading={close.isPending}
            disabledReason={typed.trim() !== name ? "Type the business name exactly to confirm." : undefined}
            onClick={() => close.mutate()}
          >
            {close.isPending ? "Closing…" : "Close business"}
          </Button>
        </>
      }
    >
      <ErrorAlert error={close.error} />
      <p className="text-sm text-fg-muted">
        <strong className="text-fg">{name}</strong> and everything in it — its website, domains and team
        access — will be removed for everyone. This can’t be undone.
      </p>
      <Field label={`Type “${name}” to confirm`}>
        {(p) => <Input {...p} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />}
      </Field>
    </Modal>
  );
}

/** "Show times in": this person's own zone for every time in the portal (kept in this browser). */
export function TimeZoneRow() {
  const { zone, businessZone, override, choose } = useTimeZoneChoice();
  const id = useId();
  const listed = (z: string) => TIME_ZONES.some(([listedZone]) => listedZone === z);
  // The saved zone and the browser's are offered even when they aren't in the short list.
  const extra = [...new Set([override, browserTimeZone()])].filter((z): z is string => !!z && !listed(z));
  return (
    <SettingRow
      icon={<Clock className="size-[15px]" strokeWidth={1.75} />}
      title={<label htmlFor={id}>Show times in</label>}
      detail={`Times are shown in ${zoneLabel(zone)}`}
    >
      <span className="relative block shrink-0">
        <select
          id={id}
          value={override ?? ""}
          onChange={(e) => choose(e.target.value || null)}
          className={cx(
            fieldControl,
            "w-auto max-w-[200px] appearance-none py-2 pr-8 text-[13px] sm:max-w-[260px]",
          )}
        >
          <option value="">
            {businessZone ? `Business time zone (${zoneLabel(businessZone)})` : "Business time zone"}
          </option>
          {[...extra.map((z): [string, string] => [z, zoneLabel(z)]), ...TIME_ZONES].map(([z, label]) => (
            <option key={z} value={z}>
              {label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-[#5c5551]"
        />
      </span>
    </SettingRow>
  );
}
